import { defineStore } from 'pinia';

const fragmentMegaMenuItemFields = `
  fragment MegaMenuItemFields on MegaMenuItem {
    cols_nb
    content
    custom_class
    icon_url
    items_id
    link
    link_type
    link_uid
    show_subcategories
    show_title
    target
    title
    uid
    width
  }
`;
var getMegaMenuItemGql = `
  query {
    MegaMenuItem {
      children {
        ...MegaMenuItemFields
        children {
          ...MegaMenuItemFields
          children {
            ...MegaMenuItemFields
          }
        }
      }
    }
  }
  ${fragmentMegaMenuItemFields}
`;

const fragmentCategoryFields = `
  fragment MegaMenuCategoryFields on CategoryTree {
    name
    position
    uid
    url_path
    url_suffix
  }
`;
function getMenuCategories({ categoryIds, parentIds, level2 }) {
    let filters = [
        ...(categoryIds === null || categoryIds === void 0 ? void 0 : categoryIds.length) ? ['$categoryIds: [String]'] : [],
        ...(parentIds === null || parentIds === void 0 ? void 0 : parentIds.length) ? ['$parentIds: [String]', '$level2: Boolean!'] : [],
    ];
    const query = `
  query getMenuCategories(${filters.join(', ')}){
    ${(categoryIds === null || categoryIds === void 0 ? void 0 : categoryIds.length) ?
        `menuCategories: categories(filters: { category_uid: { in: $categoryIds }}, pageSize: ${categoryIds.length}) {
      items {
        ...MegaMenuCategoryFields
      }
    }`
        : ''}
    ${(parentIds === null || parentIds === void 0 ? void 0 : parentIds.length) ?
        `parentCategories: categories(filters: { category_uid: { in: $parentIds }}, pageSize: ${parentIds.length}) {
        items {
          ...MegaMenuCategoryFields
          children {
            ...MegaMenuCategoryFields
            children @include(if: $level2) {
              ...MegaMenuCategoryFields
            }
          }
        }
      }`
        : ''}   
  }
  ${fragmentCategoryFields}
  `;
    const variables = {
        ...(categoryIds === null || categoryIds === void 0 ? void 0 : categoryIds.length) ? { categoryIds } : {},
        ...(parentIds === null || parentIds === void 0 ? void 0 : parentIds.length) ? { parentIds, level2 } : {}
    };
    return {
        query,
        variables
    };
}

function getIdsRecursive(items = [], level = 0, acc = { categoryIds: [], parentIds: [], level2: false }) {
    if (!items.length) {
        return acc;
    }
    return items.reduce((res, item) => {
        var _a;
        if (item.link_type === 'category') {
            if (item.show_subcategories) {
                res.parentIds.push(item.link_uid);
                if (level === 0) {
                    res.level2 = true;
                }
            }
            else {
                res.categoryIds.push(item.link_uid);
            }
        }
        if ((_a = item.children) === null || _a === void 0 ? void 0 : _a.length) {
            res = getIdsRecursive(item.children, level + 1, res);
        }
        return res;
    }, acc);
}
function buildItemsRecursive(items = [], categories = [], level = 0) {
    if (!items.length) {
        return [];
    }
    return items.map(item => {
        var _a;
        let link = null, link_type = item.link_type, children = [], hasCategoryChildren = false;
        if (item.link) {
            switch (link_type) {
                case 'category': {
                    const cat = categories.find(c => c.uid === item.link_uid);
                    link = cat ? `/${cat.url_path}${cat.url_suffix || ''}` : null;
                    if (cat && (!item.children || item.children.length === 0) && item.show_subcategories) {
                        hasCategoryChildren = true;
                        if ((_a = cat.children) === null || _a === void 0 ? void 0 : _a.length) {
                            const baseSubCat = {
                                link_type: 'category',
                                show_title: 1,
                                width: '12',
                                children: [],
                                show_subcategories: 0
                            };
                            children = cat.children.map(subcat => ({
                                ...baseSubCat,
                                link: `/${subcat.url_path}${subcat.url_suffix || ''}`,
                                link_uid: subcat.uid,
                                title: subcat.name,
                                uid: item.uid + subcat.uid,
                                ...(level === 0 && subcat.children && subcat.children.length
                                    ? {
                                        show_subcategories: 1,
                                        children: subcat.children.map(sl3 => ({
                                            ...baseSubCat,
                                            link: `/${sl3.url_path}${sl3.url_suffix || ''}`,
                                            link_uid: sl3.uid,
                                            title: sl3.name,
                                            uid: item.uid + subcat.uid + sl3.uid
                                        }))
                                    }
                                    : {})
                            }));
                        }
                    }
                    break;
                }
                case 'page': {
                    link = item.link;
                    link_type = 'internal';
                    break;
                }
                default: {
                    link = item.link;
                }
            }
        }
        if (item.children && !hasCategoryChildren) {
            children = buildItemsRecursive(item.children, categories, level + 1);
        }
        return {
            ...item,
            link,
            link_type,
            children
        };
    });
}
const useMegaMenuStore = defineStore('megaMenu', {
    state: () => ({
        items: [],
        activeItem: null
    }),
    actions: {
        async load() {
            var _a, _b, _c, _d, _e;
            const { data } = await this.$nuxt.app.$vsf.$magento.api.customQuery({ query: getMegaMenuItemGql });
            const items = ((_a = data === null || data === void 0 ? void 0 : data.MegaMenuItem) === null || _a === void 0 ? void 0 : _a.children) || [];
            if (items.length) {
                let categories = [], subCategories = [];
                const { categoryIds, parentIds, level2 } = getIdsRecursive(items);
                if (categoryIds.length || parentIds.length) {
                    const { query, variables } = getMenuCategories({ categoryIds, parentIds, level2 });
                    const { data } = await this.$nuxt.app.$vsf.$magento.api.customQuery({
                        query,
                        queryVariables: variables
                    });
                    categories = (_c = (_b = data === null || data === void 0 ? void 0 : data.menuCategories) === null || _b === void 0 ? void 0 : _b.items) !== null && _c !== void 0 ? _c : [];
                    subCategories = (_e = (_d = data === null || data === void 0 ? void 0 : data.parentCategories) === null || _d === void 0 ? void 0 : _d.items) !== null && _e !== void 0 ? _e : [];
                }
                categories = [
                    ...categories,
                    ...subCategories
                ];
                this.items = buildItemsRecursive(items, categories);
            }
            else {
                this.items = [];
            }
        },
        setActiveItem(activeItem) {
            this.activeItem = activeItem;
        }
    }
});

export { useMegaMenuStore };
